import { observer } from "mobx-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { OrderCard } from "../../components";
import "./styles.css";
import { useRootStore } from "../../stores";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { OrderStatus } from "../../interfaces_and_types";

const OrdersHistory = () => {
  const {
    sortedOldOrders,
    fetchOldOrders,
    changeOrdersStatusFetched,
    ordersStatusFetched,
    fetchOrdersStatus,
    oldOrdersFetched,
    changeOldOrdersFetched,
    ordersStatus,
    historyFilter: filter,
    historyDateFrom: dateFrom,
    historyDateTo: dateTo,
  } = useRootStore().rootStore.orderStore;
  const { fetchLoading, place, changeFetchLoading, token } =
    useRootStore().rootStore.placeStore;
  const { setCurrentPage } = useRootStore().rootStore.drawerStore;
  const list: React.LegacyRef<HTMLDivElement> = useRef(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const getOrdersStatus = async () => {
      if (token === null || place === null) return;
      if (!ordersStatusFetched && place.id !== undefined) {
        changeOrdersStatusFetched(true);
        fetchOrdersStatus(token);
      }
    };
    const getOrders = async () => {
      if (token === null || place === null) return;
      if (place.id !== undefined && !oldOrdersFetched) {
        changeFetchLoading(true);
        changeOldOrdersFetched(true);
        await fetchOldOrders(token, 0);
        changeFetchLoading(false);
      }
    };
    setCurrentPage("ordersHistory");
    getOrdersStatus();
    getOrders();

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [
    changeFetchLoading,
    changeOldOrdersFetched,
    changeOrdersStatusFetched,
    fetchOldOrders,
    fetchOrdersStatus,
    oldOrdersFetched,
    ordersStatusFetched,
    place,
    setCurrentPage,
    token,
  ]);

  const getOldOrders = async () => {
    if (token === null || place === null || sortedOldOrders === undefined)
      return;
    if (place.id !== undefined && sortedOldOrders.length > 0 && !fetchLoading) {
      setButtonLoading(true);
      await fetchOldOrders(token, sortedOldOrders.length);
      setButtonLoading(false);
    }
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (
      sortedOldOrders !== undefined &&
      scrollHeight - scrollTop - clientHeight < 1
    ) {
      ordersStatus.isReady + ordersStatus.refused > sortedOldOrders.length &&
        getOldOrders();
    }
  };

  const filteredOrders = useMemo(() => {
    const fromDate = new Date(dateFrom);
    fromDate.setHours(0, 0, 0, 0);
    const toDate = new Date(dateTo);
    toDate.setHours(23, 59, 59, 999);

    return sortedOldOrders.filter((order) => {
      const orderDate = new Date(order.orderDate);
      const inRange = orderDate >= fromDate && orderDate <= toDate;
      if (!inRange) return false;

      if (filter === "done") {
        return (
          order.status === OrderStatus.PICKEDUP ||
          order.status === OrderStatus.COMPLETED ||
          order.status === OrderStatus.PREPARED
        );
      }
      if (filter === "cancelled") {
        return order.status === OrderStatus.CANCELLED;
      }
      return true;
    });
  }, [sortedOldOrders, dateFrom, dateTo, filter]);

  if (!fetchLoading && sortedOldOrders !== undefined)
    return (
      <div className="orders-history">
        <div
          ref={list}
          className="oldOrderCards-container"
          onScroll={handleScroll}
        >
          {filteredOrders.map((order) =>
            order.status === OrderStatus.CANCELLED ? (
              <div key={order.id} className="history-cancelled-card">
                <OrderCard order={order} />
                <div className="history-cancelled-badge">{t("Cancelled")}</div>
              </div>
            ) : order.isReady || order.status >= OrderStatus.PREPARED ? (
              <div key={order.id} className="history-done-wrapper">
                <OrderCard order={order} />
                <div className="history-done-badge">{t("Done")}</div>
              </div>
            ) : (
              <OrderCard key={order.id} order={order} />
            ),
          )}
          {filteredOrders.length === 0 && (
            <p className="history-empty">{t("No orders found")}</p>
          )}
          {!buttonLoading && filteredOrders.length > 0 && (
            <button
              className="orders-history-loadMore"
              onClick={() => getOldOrders()}
            >
              {t("Load more")}
            </button>
          )}
        </div>
        {buttonLoading && (
          <CircularProgress
            className="MuiCircularProgress-colorPrimary"
            size={50}
            style={{ position: "fixed", bottom: 0 }}
          />
        )}
      </div>
    );
  else
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100vw",
          height: "100vh",
        }}
      >
        <CircularProgress
          className="MuiCircularProgress-colorPrimary"
          size={50}
        />
      </div>
    );
};

export default observer(OrdersHistory);
